import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import { saveAs } from 'file-saver';
import ReactApexChart from 'react-apexcharts';
// material
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Select,
  MenuItem,
  Stack,
  CircularProgress
} from '@material-ui/core';
//
import { useTheme } from '@material-ui/core/styles';
import { styled } from '@mui/styles';

import apiHandler from '../../../api/axios-api';

// ----------------------------------------------------------------------

const StyledSelect = styled(Select)(({ theme }) => ({
  // fontWeight: 'bold',
  fontSize: '14px',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  // fontWeight: 'bold',
  fontSize: '14px',
}));

DeviceNutrientHistorical.propTypes = {
  deviceId: PropTypes.string.isRequired
};

export default function DeviceNutrientHistorical({ deviceId }) {
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneOffset = new Date().getTimezoneOffset() * 60 * 1000;
  const theme = useTheme();
  const [chartPeriod, setChartPeriod] = useState(1);
  const [chartSeries, setChartSeries] = useState([]);
  const [timezone, setTimezone] = useState(timezoneName);
  const [updating, setUpdating] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadData = (period = 0) => {
    setDownloading(true);
    apiHandler(`devices/${deviceId}/historical/export?p=${period}&tz=${timezone}`, { responseType: 'blob' }).then(
      (res) => {
        saveAs(res.data, `historical-${deviceId}.csv`);
        setDownloading(false);
      }
    );
  };

  const fetchData = useCallback((period = 0) => {
    setUpdating(true);
    apiHandler(`devices/${deviceId}/historical?p=${period}&tz=${timezone}`).then((res) => {
      const chartSeriesData = [];
      const temperatureSerie = [];
      const humiditySerie = [];
      const co2Serie = [];
      const intensitySerie = [];
      res.data.time.map((t, i) => {
        const unixTime = Date.parse(t) - timezoneOffset;
        temperatureSerie.push([unixTime, res.data.temperature[i]]);
        humiditySerie.push([unixTime, res.data.humidity[i]]);
        co2Serie.push([unixTime, res.data.co2[i]]);
        intensitySerie.push([unixTime, res.data.intensity[i]]);
        return null;
      });

      if (temperatureSerie !== []) {
        chartSeriesData.push({ name: 'Temperature', data: temperatureSerie });
      }
      if (humiditySerie !== []) {
        chartSeriesData.push({ name: 'Humidity', data: humiditySerie });
      }
      if (co2Serie !== []) {
        chartSeriesData.push({ name: 'CO2', data: co2Serie });
      }
      if (intensitySerie !== []) {
        chartSeriesData.push({ name: 'Intensity', data: intensitySerie });
      }
      setChartSeries(chartSeriesData);
      setUpdating(false);
    });
  }, [deviceId]);

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const options = {
    colors: [
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.error.main,
      theme.palette.success.main
    ],
    chart: {
      toolbar: {
        show: false
      }
    },
    stroke: {
      width: 3
    },
    dataLabels: {
      enabled: false
    },
    xaxis: {
      type: 'datetime'
    },
    yaxis: [
      {
        title: {
          text: 'Temperature'
        },
        seriesName: 'Temperature'
      },
      {
        title: {
          text: 'Humidity'
        },
        seriesName: 'Humidity'
      },
      {
        title: {
          text: 'CO2'
        },
        opposite: true,
        seriesName: 'CO2'
      },
      {
        title: {
          text: 'Intensity'
        },
        opposite: true,
        seriesName: 'Intensity'
      }
    ],
    tooltip: {
      x: {
        format: 'dd/MMM/yyyy hh:mm:ss'
      }
    }
  };

  return (
    <Card>
      <CardHeader title="Historical" subheader="Nutrient and Temperature" />
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <ToggleButtonGroup
            value={chartPeriod}
            exclusive
            onChange={(event, period) => {
              setChartPeriod(period);
              fetchData(period);
            }}
            aria-label="text alignment"
          >
            <ToggleButton disabled={updating} value={1} aria-label="24 Hours" size="small">
              &nbsp;&nbsp;&nbsp;1 Day&nbsp;&nbsp;&nbsp;
            </ToggleButton>
            <ToggleButton disabled={updating} value={2} aria-label="2 Days" size="small">
              &nbsp;&nbsp;&nbsp;2 Days&nbsp;&nbsp;&nbsp;
            </ToggleButton>
            <ToggleButton disabled={updating} value={3} aria-label="7 Days" size="small">
              &nbsp;&nbsp;&nbsp;7 Days&nbsp;&nbsp;&nbsp;
            </ToggleButton>
            <ToggleButton disabled={updating} value={4} aria-label="30 Days" size="small">
              &nbsp;&nbsp;&nbsp;30 Days&nbsp;&nbsp;&nbsp;
            </ToggleButton>
            <ToggleButton disabled={updating} value={5} aria-label="3 Months" size="small">
              &nbsp;&nbsp;&nbsp;3 Months&nbsp;&nbsp;&nbsp;
            </ToggleButton>
            <ToggleButton disabled={updating} value={6} aria-label="6 Months" size="small">
              &nbsp;&nbsp;&nbsp;6 Months&nbsp;&nbsp;&nbsp;
            </ToggleButton>
          </ToggleButtonGroup>
          <Stack direction="row" alignItems="center">
            <StyledSelect
              size="small"
              value={timezone}
              disabled={downloading}
              onChange={(event, tz) => {
                setTimezone(event.target.value);
                // TODO: save timezone to cookies
              }}
              aria-label="text alignment"
            >
              <StyledMenuItem value={timezoneName} size="small">{timezoneName}</StyledMenuItem>
              <StyledMenuItem value={'Etc/UTC'} size="small">UTC</StyledMenuItem>
            </StyledSelect>
            &nbsp;&nbsp;&nbsp;
            <Button
              variant="contained"
              disabled={downloading}
              onClick={() => {
                downloadData(chartPeriod);
              }}
            >
              {downloading ? 'Generating Report..' : 'Download CSV Report'}&nbsp;&nbsp;&nbsp;
              {downloading && <CircularProgress color="grey" size={20} />}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <ReactApexChart type="line" series={chartSeries} options={options} height={400} />
      </Box>
    </Card>
  );
}
